import { useState } from "react";
import { Link } from "react-router-dom";
import { Linkedin, Twitter, Youtube, Github, Mail, Phone, MapPin } from "lucide-react";
import { toast } from "sonner";
import { COMPANY } from "@/data/content";
import { api } from "@/lib/api";

export const Footer = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await api.post("/newsletter", { email });
      toast.success("Subscribed. Welcome to Akyvia insights.");
      setEmail("");
    } catch (err) {
      toast.error(err?.response?.data?.detail?.[0]?.msg || "Invalid email");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer data-testid="site-footer" className="bg-[#0B1B3D] text-slate-200 mt-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-10">
          {/* Brand */}
          <div className="lg:col-span-4">
            <div className="flex items-center gap-3 mb-5">
              <img
                src="/brand/akyvia-logo.png"
                alt="Akyvia Sciences and Technologies"
                className="w-12 h-12 rounded-full object-contain ring-1 ring-white/15 shadow-md"
              />
              <div className="leading-tight">
                <div className="font-display font-semibold text-white tracking-tight">
                  {COMPANY.short.toUpperCase()}
                </div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-sky-300/80">
                  Sciences & Technologies
                </div>
              </div>
            </div>
            <p className="text-sm text-slate-300/85 leading-relaxed max-w-sm">
              A research-driven company engineering the future through advanced
              motors, intelligent systems, aerospace and scientific innovation.
            </p>

            <div className="flex items-center gap-3 mt-6">
              {[
                { href: COMPANY.social.linkedin, Icon: Linkedin, k: "li" },
                { href: COMPANY.social.twitter, Icon: Twitter, k: "tw" },
                { href: COMPANY.social.youtube, Icon: Youtube, k: "yt" },
                { href: COMPANY.social.github, Icon: Github, k: "gh" },
              ].map(({ href, Icon, k }) => (
                <a
                  key={k}
                  href={href}
                  data-testid={`footer-social-${k}`}
                  target="_blank"
                  rel="noreferrer"
                  className="w-9 h-9 rounded-md border border-white/10 flex items-center justify-center text-slate-300 hover:text-amber-400 hover:border-amber-400/50 transition-colors"
                >
                  <Icon className="w-4 h-4" strokeWidth={1.5} />
                </a>
              ))}
            </div>
          </div>

          {/* Links */}
          <div className="lg:col-span-2">
            <h4 className="font-display text-white text-sm font-semibold mb-4">
              Company
            </h4>
            <ul className="space-y-3 text-sm text-slate-300/85">
              <li><Link to="/about" className="hover:text-amber-400">About</Link></li>
              <li><Link to="/careers" className="hover:text-amber-400">Careers</Link></li>
              <li><Link to="/news" className="hover:text-amber-400">News</Link></li>
              <li><Link to="/contact" className="hover:text-amber-400">Contact</Link></li>
            </ul>
          </div>

          <div className="lg:col-span-2">
            <h4 className="font-display text-white text-sm font-semibold mb-4">
              Research
            </h4>
            <ul className="space-y-3 text-sm text-slate-300/85">
              <li><Link to="/research" className="hover:text-amber-400">R & D</Link></li>
              <li><Link to="/technologies" className="hover:text-amber-400">Technologies</Link></li>
              <li><Link to="/projects" className="hover:text-amber-400">Projects</Link></li>
              <li><Link to="/products" className="hover:text-amber-400">Products</Link></li>
            </ul>
          </div>

          <div className="lg:col-span-4">
            <h4 className="font-display text-white text-sm font-semibold mb-4">
              Stay in the loop
            </h4>
            <p className="text-sm text-slate-300/80 mb-4">
              Research updates, product releases and training announcements.
            </p>
            <form onSubmit={onSubscribe} className="flex flex-col sm:flex-row gap-2">
              <input
                data-testid="newsletter-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                className="flex-1 bg-white/5 border border-white/10 rounded-md px-4 py-3 text-sm text-white placeholder:text-slate-400 focus:outline-none focus:border-amber-400/70"
              />
              <button
                data-testid="newsletter-subscribe-button"
                type="submit"
                disabled={loading}
                className="bg-amber-500 hover:bg-amber-400 disabled:opacity-60 text-[#0B1B3D] font-semibold px-5 py-3 rounded-md text-sm transition-colors"
              >
                {loading ? "..." : "Subscribe"}
              </button>
            </form>

            <div className="mt-6 grid gap-2 text-sm text-slate-300/85">
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-sky-300" strokeWidth={1.5} />
                <span>{COMPANY.email}</span>
              </div>
              <div className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-sky-300" strokeWidth={1.5} />
                <span>{COMPANY.phone}</span>
              </div>
              <div className="flex items-start gap-2">
                <MapPin className="w-4 h-4 text-sky-300 mt-0.5" strokeWidth={1.5} />
                <span>{COMPANY.address}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-white/10 mt-14 pt-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-3 text-xs text-slate-400">
          <span data-testid="footer-copyright">
            © {new Date().getFullYear()} {COMPANY.name}. All rights reserved.
          </span>
          <div className="flex items-center gap-5">
            <Link to="/privacy" className="hover:text-amber-400">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-amber-400">Terms & Conditions</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
